import React, { useState } from 'react';
import { MessageSquare, PanelRightClose, PanelRightOpen } from 'lucide-react';
import Sidebar from './Sidebar';
import ChatInterface from './ChatInterface';
import PreviewPane from './PreviewPane';
import { useTheme } from '../contexts/ThemeContext';

const SplitView = ({
  conversations,
  activeConversation,
  onSelectConversation,
  onNewConversation,
  onSendMessage,
  onDeleteConversation,
  onRenameConversation,
  brandMeta,
  brandProfileSpec,
  brandApplicationSlice,
  brandContextPacks,
  brandVisualTheme,
  onSwitchView,
  sessionUser,
  onLogin,
  onLogout,
  authStatus,
  accountLabel,
  portalBaseUrl,
  brands,
  activeBrandId,
  onBrandChange,
  availableEnvironments,
  activeEnvironment,
  onEnvironmentChange,
  onOpenResearch,
  onOpenSiteAgent,
  onUpdateUser,
  streamingMessage,
  onBranchMessage,
  onOpenBranchTree,
  branchIndex = 1,
  totalBranches = 1,
}) => {
  const [previewOpen, setPreviewOpen] = useState(true);
  const { resolvedTheme } = useTheme();
  const isBrandTheme = resolvedTheme === 'noderiver';
  const themeMode = brandVisualTheme?.modes?.[resolvedTheme] ?? brandVisualTheme?.modes?.light;

  const baseBackground =
    resolvedTheme === 'light'
      ? '#ffffff'
      : themeMode?.background ?? '#f8fafc';

  const shellStyle = isBrandTheme
    ? { background: brandVisualTheme?.gradients?.hero, color: brandVisualTheme?.modes?.noderiver?.text ?? '#ffffff' }
    : { backgroundColor: baseBackground };

  const shellClass = 'h-screen flex overflow-hidden';
  const columnBorder = isBrandTheme ? 'border-white/10' : 'border-gray-200 dark:border-gray-800';
  const headerBg = isBrandTheme
    ? 'bg-white/5 text-white'
    : 'bg-white dark:bg-gray-900 text-gray-600 dark:text-gray-200';

  return (
    <div className={shellClass} style={shellStyle}>
      <Sidebar
        conversations={conversations}
        activeConversation={activeConversation}
        onSelectConversation={onSelectConversation}
        onNewConversation={onNewConversation}
        onDeleteConversation={onDeleteConversation}
        onRenameConversation={onRenameConversation}
        brandMeta={brandMeta}
        brandProfileSpec={brandProfileSpec}
        brandApplicationSlice={brandApplicationSlice}
        brandContextPacks={brandContextPacks}
        brandVisualTheme={brandVisualTheme}
        sessionUser={sessionUser}
        onLogin={onLogin}
        onLogout={onLogout}
        authStatus={authStatus}
        accountLabel={accountLabel}
        portalBaseUrl={portalBaseUrl}
        brands={brands}
        activeBrandId={activeBrandId}
        onBrandChange={onBrandChange}
        availableEnvironments={availableEnvironments}
        activeEnvironment={activeEnvironment}
        onEnvironmentChange={onEnvironmentChange}
        onOpenResearch={onOpenResearch}
        onOpenSiteAgent={onOpenSiteAgent}
        onUpdateUser={onUpdateUser}
      />
      <div className="flex-1 flex">
        <div
          className={`${previewOpen ? 'w-1/2' : 'w-full'} border-r ${columnBorder} transition-all duration-300`}
        >
          <div
            className={`border-b ${columnBorder} px-4 py-2 flex items-center justify-between ${headerBg}`}
          >
            <button
              onClick={onSwitchView}
              className={`flex items-center space-x-2 px-3 py-1.5 rounded-lg text-sm transition-colors ${
                isBrandTheme
                  ? 'hover:bg-white/10 text-white'
                  : 'hover:bg-gray-100 dark:hover:bg-gray-800'
              }`}
            >
              <MessageSquare className="w-4 h-4" />
              <span>Simple View</span>
            </button>
            <button
              onClick={() => setPreviewOpen(!previewOpen)}
              className={`flex items-center space-x-2 px-3 py-1.5 rounded-lg text-sm transition-colors ${
                isBrandTheme
                  ? 'hover:bg-white/10 text-white'
                  : 'text-gray-600 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800'
              }`}
              title={previewOpen ? 'Close preview' : 'Open preview'}
            >
              {previewOpen ? (
                <>
                  <PanelRightClose className="w-4 h-4" />
                  <span>Close Preview</span>
                </>
              ) : (
                <>
                  <PanelRightOpen className="w-4 h-4" />
                  <span>Open Preview</span>
                </>
              )}
            </button>
          </div>
          <div className="h-[calc(100vh-48px)]">
            <ChatInterface
              conversation={activeConversation}
              onSendMessage={onSendMessage}
              brandVisualTheme={brandVisualTheme}
              user={sessionUser}
              streamingMessage={streamingMessage}
              onBranchMessage={onBranchMessage}
              onOpenBranchTree={onOpenBranchTree}
              branchIndex={branchIndex}
              totalBranches={totalBranches}
            />
          </div>
        </div>
        {previewOpen && (
          <div className={`w-1/2 transition-all duration-300 border-l ${columnBorder}`}>
            <PreviewPane 
              conversation={activeConversation} 
              brandVisualTheme={brandVisualTheme}
              brandMeta={brandMeta}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default SplitView;
